import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import './SelectInput.scss';

interface SelectInputProps {
  label: string;
  value: any;
  options: { label: string; value: any }[];
  required?: boolean;
  disabled?: boolean;
  small?: boolean;
  onChange: (value: any) => void;
}

const SelectInput: React.FC<SelectInputProps> = ({ label, value, options, required, disabled, small, onChange }) => {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedLabel = options.find(o => o.value === value)?.label ?? '';
  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inContainer = containerRef.current?.contains(target) ?? false;
      const inPanel = panelRef.current?.contains(target) ?? false;
      if (!inContainer && !inPanel) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const open = () => {
    if (!disabled) {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) {
        setPanelStyle({ position: 'fixed', top: rect.bottom, left: rect.left, minWidth: rect.width, zIndex: 9999 });
      }
      setIsOpen(true);
      setSearch('');
    }
  };
  const close = () => { setIsOpen(false); setSearch(''); };

  const handleSelect = (optValue: any) => {
    onChange(optValue);
    close();
  };

  return (
    <div className={`mb-3 select-input${small ? ' select-input--sm' : ''}`} ref={containerRef}>
      <label className="form-label">
        {label}
        {required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
      </label>
      <div
        ref={triggerRef}
        className={`form-select${small ? ' form-select-sm' : ''} select-input__trigger${isOpen ? ' select-input__trigger--open' : ''}${disabled ? ' disabled' : ''}`}
        onClick={open}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') open();
          if (e.key === 'Escape') close();
        }}
      >
        {selectedLabel || <span className="select-input__placeholder">{t('select')}</span>}
      </div>

      {isOpen && ReactDOM.createPortal(
        <div
          ref={panelRef}
          className={`select-input__panel${small ? ' select-input__panel--sm' : ''}`}
          style={panelStyle}
        >
          <div className="select-input__search-wrap">
            <input
              ref={searchRef}
              type="text"
              className="form-control form-control-sm"
              placeholder={t('search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') close(); }}
            />
          </div>
          <div className="select-input__list">
            {filtered.length === 0 ? (
              <div className="select-input__no-results">{t('noResults')}</div>
            ) : (
              filtered.map(opt => (
                <div
                  key={opt.value}
                  className={`select-input__option${opt.value === value ? ' select-input__option--selected' : ''}`}
                  onMouseDown={() => handleSelect(opt.value)}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SelectInput;
